/* 
Copyright (c) Karsten Jeske and John Firth
This source code is licensed under the proprietary license found in the
LICENSE file in the root directory of this source tree.
*/

import { InfoPopover } from "components/InfoPopover"
import {
  selectComputedRanges,
  selectInitialScrsDataReady,
} from "features/initialScrs/InitialScrsSlice"
import {
  selectHistoricalDataFilters,
  setCapeModel,
  setCapeRange,
  setDrawdownRange,
  setYearRange,
} from "features/results/HistoricalDataFiltersSlice"
import { selectUiTables, setResultsFresh } from "features/results/ResultsSlice"
import Slider from "rc-slider"
import React from "react"
import { useDispatch, useSelector } from "react-redux"
import "rc-slider/assets/index.css"

// AIDEV-NOTE: Keep slider/event handler inputs readonly to satisfy strict correctness linting without changing filter behavior.

// Define step sizes as constants to ensure consistency
export const STEP_SIZES = {
  YEAR: 1,
  DRAWDOWN: 0.01,
  CAPE: 1.0,
  // Display properties
  DISPLAY: {
    DRAWDOWN_DECIMALS: 0, // Force whole percentage display
  },
}

const HistoricalDataFilters: React.FC = () => {
  const dispatch = useDispatch()
  const filters = useSelector(selectHistoricalDataFilters)
  const computedRanges = useSelector(selectComputedRanges)
  const dataReady = useSelector(selectInitialScrsDataReady)
  const { uiTables } = useSelector(selectUiTables)
  const retirementCount = uiTables?.userFiltersRetirementCount

  const handleCapeChange = (event: unknown) => {
    const e = event as React.ChangeEvent<HTMLInputElement>
    const newCapeModel = e.target.value as "shiller" | "ern"
    dispatch(setCapeModel(newCapeModel))

    const newRange =
      newCapeModel === "ern" ? computedRanges.capeRanges.ern : computedRanges.capeRanges.shiller

    if (newRange) {
      let newMinCape = filters.minCape
      let newMaxCape = filters.maxCape
      let needsUpdate = false

      // Adjust min if needed
      if (filters.minCape < newRange.min) {
        newMinCape = newRange.min
        needsUpdate = true
      }

      // Adjust max if needed
      if (filters.maxCape > newRange.max) {
        newMaxCape = newRange.max
        needsUpdate = true
      }

      // Only dispatch if we need to adjust the values
      if (needsUpdate) {
        dispatch(setCapeRange({ minCape: newMinCape, maxCape: newMaxCape }))
      }
    }

    // Mark tables as stale to trigger recalculation
    dispatch(setResultsFresh(false))
  }

  const handleYearRangeChange = (values: readonly number[]) => {
    if (values.length === 2) {
      const [first, last] = values
      dispatch(setYearRange({ firstStartingYear: first, lastStartingYear: last }))
      dispatch(setResultsFresh(false))
    }
  }

  const handleDrawdownRangeChange = (values: readonly number[]) => {
    if (values.length === 2) {
      const [min, max] = values
      dispatch(setDrawdownRange({ minAbsDrawdown: min, maxAbsDrawdown: max }))
      dispatch(setResultsFresh(false))
    }
  }

  const handleCapeRangeChange = (values: readonly number[]) => {
    if (values.length === 2) {
      const [min, max] = values
      dispatch(setCapeRange({ minCape: min, maxCape: max }))
      dispatch(setResultsFresh(false))
    }
  }

  const normalizeRangeValues = (values: number | readonly number[]): readonly number[] => {
    if (typeof values === "number") {
      return [values, values]
    }
    return values
  }

  // Helper function to format value with the appropriate number of decimal places based on step size
  const formatWithStepDecimals = (value: number, stepSize: number): string => {
    const decimalPlaces = stepSize < 1 ? String(stepSize).split(".")[1].length : 0
    return value.toFixed(decimalPlaces)
  }

  // Helper function specifically for CAPE values
  const formatCape = (value: number): string => {
    return formatWithStepDecimals(value, STEP_SIZES.CAPE)
  }

  // Helper function specifically for Drawdown values - includes percentage formatting
  const formatDrawdown = (value: number): string => {
    // Use the specified display decimals instead of calculating from step size
    return (value * 100).toFixed(STEP_SIZES.DISPLAY.DRAWDOWN_DECIMALS) + "%"
  }

  // Helper function to round min down and max up to the nearest step
  const getRoundedBounds = (min: number, max: number, step: number): [number, number] => {
    // Calculate how many steps from 0 the min value is, then floor it to round down
    const stepsFromZeroToMin = Math.floor(min / step)
    const roundedMin = stepsFromZeroToMin * step

    // Calculate how many steps from 0 the max value is, then ceil it to round up
    const stepsFromZeroToMax = Math.ceil(max / step)
    const roundedMax = stepsFromZeroToMax * step

    return [roundedMin, roundedMax]
  }

  // Use computed ranges from the store if available
  const yearRawBounds = computedRanges.years
    ? [computedRanges.years.min, computedRanges.years.max]
    : [filters.firstStartingYear, filters.lastStartingYear]

  // Round year bounds to the nearest 1 (since years are integers)
  const yearBounds = getRoundedBounds(yearRawBounds[0], yearRawBounds[1], STEP_SIZES.YEAR)

  const drawdownRawBounds = computedRanges.absoluteDrawdowns
    ? [computedRanges.absoluteDrawdowns.min, computedRanges.absoluteDrawdowns.max]
    : [0, 1] // Default range from 0% to 100%

  // Round drawdown bounds to the nearest step
  const drawdownBounds = getRoundedBounds(
    drawdownRawBounds[0],
    drawdownRawBounds[1],
    STEP_SIZES.DRAWDOWN
  )

  // Get the current CAPE range bounds based on the selected model
  const selectedCapeRange =
    filters.cape === "ern" ? computedRanges.capeRanges.ern : computedRanges.capeRanges.shiller

  // Get the raw bounds for the CAPE slider
  const capeRawBounds = selectedCapeRange
    ? [selectedCapeRange.min, selectedCapeRange.max]
    : [filters.minCape, filters.maxCape]

  // Round CAPE bounds to the nearest whole number for display purposes
  const capeBounds = getRoundedBounds(capeRawBounds[0], capeRawBounds[1], STEP_SIZES.CAPE)

  return (
    <fieldset className="form-group mb-4" id="historical-retirement-filters-fieldset">
      <legend className="mb-3">
        Historical Data Selection
        <InfoPopover
          htmlContent={true}
          content="<p>Limits to apply to the historical data used in the retirement simulations.</p>
                   <p>For example, if the year range is 1926-2000, the drawdown range is 5%-80%, and 
                      the CAPE ratio range is 0-20, then the only retirements simulated will be those
                      starting in months between 1926 and 2000, where the S&P 500 was initially 
                      5% to 80% below its previous all-time high, and the CAPE ratio was initially 
                      between 0 and 20.
                   </p>
                   <p>Bear in mind that reducing the number of simulations too much could outweigh
                    the advantage of using limits that are similar to current market conditions.
                   </p>"
        />
      </legend>
      <div className="row">
        {/* Year Range Slider */}
        <fieldset className="col-12 col-sm-6 col-md-4 col-lg-3 mb-2">
          <legend className="col-form-label">
            Years
            <InfoPopover content="Limit simulated retirements by the years in which they begin." />
          </legend>
          <div className="px-2">
            {/* Min/Max labels above the slider */}
            <div className="d-flex justify-content-between small text-muted mb-1">
              <span>{yearBounds[0]}</span>
              <span>{yearBounds[1]}</span>
            </div>
            <Slider
              range
              min={yearBounds[0]}
              max={yearBounds[1]}
              step={STEP_SIZES.YEAR}
              value={[filters.firstStartingYear, filters.lastStartingYear]}
              onChange={(values: number | readonly number[]) => {
                handleYearRangeChange(normalizeRangeValues(values))
              }}
              className="mb-2"
              aria-label="Year range selection"
              aria-valuetext={`From ${filters.firstStartingYear} to ${filters.lastStartingYear}`}
              disabled={!dataReady}
            />
            <div className="small text-muted mt-1 mb-2" aria-live="polite">
              {filters.firstStartingYear} - {filters.lastStartingYear}
            </div>
          </div>
        </fieldset>

        {/* Drawdown Range Slider */}
        <fieldset className="col-12 col-sm-6 col-md-4 col-lg-3 mb-2">
          <legend className="col-form-label">
            S&P 500 Drawdowns
            <InfoPopover content="Limit simulated retirements by the market drawdown in their starting months." />
          </legend>
          <div className="px-2">
            {/* Min/Max labels above the slider */}
            <div className="d-flex justify-content-between small text-muted mb-1">
              <span>{formatDrawdown(drawdownBounds[0])}</span>
              <span>{formatDrawdown(drawdownBounds[1])}</span>
            </div>
            <Slider
              range
              min={drawdownBounds[0]}
              max={drawdownBounds[1]}
              step={STEP_SIZES.DRAWDOWN}
              value={[filters.minAbsDrawdown, filters.maxAbsDrawdown]}
              onChange={(values: number | readonly number[]) => {
                handleDrawdownRangeChange(normalizeRangeValues(values))
              }}
              className="mb-2"
              aria-label="Drawdown range selection"
              aria-valuetext={`From ${formatDrawdown(filters.minAbsDrawdown)} to ${formatDrawdown(filters.maxAbsDrawdown)}`}
              disabled={!dataReady}
            />
            <div className="small text-muted mt-1 mb-2" aria-live="polite">
              {formatDrawdown(filters.minAbsDrawdown)} - {formatDrawdown(filters.maxAbsDrawdown)}
            </div>
          </div>
        </fieldset>

        {/* CAPE Range Slider */}
        <fieldset className="col-12 col-sm-6 col-md-4 col-lg-3 mb-2">
          <legend className="col-form-label">
            CAPE Ratios
            <InfoPopover content="Limit simulated retirements by the CAPE ratio in their starting months." />
          </legend>
          <div className="px-2">
            {/* Min/Max labels above the slider */}
            <div className="d-flex justify-content-between small text-muted mb-1">
              <span>{formatCape(capeBounds[0])}</span>
              <span>{formatCape(capeBounds[1])}</span>
            </div>
            <Slider
              range
              min={capeBounds[0]}
              max={capeBounds[1]}
              step={STEP_SIZES.CAPE}
              value={[filters.minCape, filters.maxCape]}
              onChange={(values: number | readonly number[]) => {
                handleCapeRangeChange(normalizeRangeValues(values))
              }}
              className="mb-2"
              aria-label="CAPE range selection"
              aria-valuetext={`From ${formatCape(filters.minCape)} to ${formatCape(filters.maxCape)}`}
              disabled={!dataReady}
            />
            <div className="small text-muted mt-1 mb-2" aria-live="polite">
              {formatCape(filters.minCape)} - {formatCape(filters.maxCape)}
            </div>
          </div>
        </fieldset>

        <fieldset className="col-12 col-sm-6 col-md-4 col-lg-3 mb-2">
          <legend className="col-form-label">
            CAPE Model
            <InfoPopover
              content="The model to use when filtering historical cohorts by their initial CAPE ratios. <a href='https://earlyretirementnow.com/2022/10/05/building-a-better-cape-ratio/' target='_blank'>Learn more</a>."
              htmlContent={true}
            />
          </legend>
          <div className="form-check form-check-inline">
            <input
              className="form-check-input"
              type="radio"
              id="ern-cape"
              value="ern"
              checked={filters.cape === "ern"}
              onChange={handleCapeChange}
              aria-label="Use ERN CAPE model"
              disabled={!dataReady}
            />
            <label className="form-check-label" htmlFor="ern-cape">
              ERN
            </label>
          </div>
          <div className="form-check form-check-inline">
            <input
              className="form-check-input"
              type="radio"
              id="ern-shiller"
              value="shiller"
              checked={filters.cape === "shiller"}
              onChange={handleCapeChange}
              aria-label="Use Shiller CAPE model"
              disabled={!dataReady}
            />
            <label className="form-check-label" htmlFor="ern-shiller">
              Shiller
            </label>
          </div>
        </fieldset>
      </div>
      {retirementCount !== undefined && (
        <div className="text-start small text-muted mt-2 ms-2">
          {retirementCount.toLocaleString()} retirements simulated with these filters
        </div>
      )}
    </fieldset>
  )
}

export default HistoricalDataFilters
